import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import { useQuery } from '@tanstack/react-query';

import SoftBox from 'components/SoftComponents/SoftBox';
import SoftTypography from 'components/SoftComponents/SoftTypography';

import { getAllDevices } from 'services/api/bks';

import 'mapbox-gl/dist/mapbox-gl.css';

// props-types is a library for typechecking of props
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Box from '@mui/material/Box';

import { center, bbox, bboxPolygon } from "@turf/turf";
import geoViewport from "@mapbox/geo-viewport";
import { useNavigate } from 'react-router-dom';
import { group_outliners } from 'utils/group_outliners';
import { setGroupPolygonColour } from 'utils/map';
import { setDevicePointColour } from 'utils/map';
import { useSoftUIController } from 'hooks/context';

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function setGroupFeatures(group, showAdvancedDeviceLevel) {
    const { geojson, ...metadata } = group;

    var group_features = geojson;

    group_features.forEach((feature) => {
        feature.properties = {
            ...metadata,
            access: showAdvancedDeviceLevel,
            feature_colour: setGroupPolygonColour(group, showAdvancedDeviceLevel)
        };
    });

    return group_features;
}

function setDeviceFeatures(device, showAdvancedDeviceLevel) {
    return {
        type: "Feature",
        properties: {
            ...device,
            access: showAdvancedDeviceLevel,
            ...setDevicePointColour(device, showAdvancedDeviceLevel)
        },
        geometry: {
            type: 'Point',
            coordinates: device.position
        }
    }
}


function ClearwaysSubgroupsMap({ map, user, groups_data, showDevices }) {

    const navigate = useNavigate();

    const [controller] = useSoftUIController();
    const { userPermissions } = controller;

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
    });

    const handleSubgroupClick = (e) => {
        e.preventDefault();
        popup.remove();
        navigate("/overview/" + e.features[0].properties.parent_id + "/" + e.features[0].properties.group_id);
    }

    const handleDeviceClick = (e) => {
        e.preventDefault();
        popup.remove();
        navigate("/devices/" + e.features[0].properties.uuid);
    }

    const handleMapClick = (e) => {
        if (!e.defaultPrevented) {
            popup.remove();
            navigate("/overview");
        }
    }

    const handleSubgroupMouseEnter = (e) => {
        map.current.getCanvas().style.cursor = 'pointer';
    }

    const handleSubgroupMouseLeave = (e) => {
        map.current.getCanvas().style.cursor = '';
    }

    const handleDeviceMouseEnter = (e) => {
        map.current.getCanvas().style.cursor = 'pointer';

        const coordinates = e.features[0].geometry.coordinates.slice();
        var description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b></div>`;
        if (showAdvancedDeviceLevel) {
            description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b><br><div style='font-size:13px'>${e.features[0].properties.name}</div></div>`;
        }

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        popup.setLngLat(coordinates).setHTML(description).addTo(map.current);
    }

    const handleDeviceMouseLeave = (e) => {
        map.current.getCanvas().style.cursor = '';
        popup.remove();
    }

    useEffect(() => { //mount
        if (!map.current) return;

        var existing_device_source = map.current.getSource('devices-source');
        var existing_device_layer = map.current.getLayer('devices-layer');

        if (!existing_device_source) {
            map.current.addSource('devices-source', {
                type: 'geojson',
                data: {
                    "type": "FeatureCollection",
                    "features": groups_data.devices.map(device => setDeviceFeatures(device, showAdvancedDeviceLevel)).sort((a, b) => { return a.properties.zIndex - b.properties.zIndex })
                }
            });
        }

        if (!existing_device_layer) {
            map.current.addLayer({
                id: 'devices-layer',
                type: 'circle',
                source: 'devices-source',
                paint: {
                    'circle-color': ['get', 'feature_colour'],
                    'circle-radius': 9,
                    'circle-stroke-width': 3,
                    'circle-stroke-color': '#ffffff'
                }
            });
        }

        var existing_base_layer = map.current.getLayer('groups-layer');
        var existing_base_source = map.current.getSource('groups-source');

        if (!existing_base_source) {
            map.current.addSource('groups-source', {
                type: 'geojson',
                data: {
                    "type": "FeatureCollection",
                    "features": setGroupFeatures(groups_data, showAdvancedDeviceLevel)
                }
            });
        }
        if (!existing_base_layer) {
            map.current.addLayer({
                id: 'groups-layer',
                type: 'fill',
                source: 'groups-source',
                paint: {
                    "fill-opacity": 0.1,
                    "fill-color": "white"
                }
            }).addLayer({
                id: 'groups-layer-liner',
                type: 'line',
                source: 'groups-source',
                paint: {
                    "line-color": "white",
                    "line-width": 2,
                    "line-opacity": 0.2
                }
            });
        }

        if (groups_data.subgroups.length > 0) {
            var existing_group_source = map.current.getSource('subgroups-source');
            var existing_group_layer = map.current.getLayer('subgroups-layer');

            if (!existing_group_source) {
                map.current.addSource('subgroups-source', {
                    type: 'geojson',
                    data: {
                        "type": "FeatureCollection",
                        "features": groups_data.subgroups.flatMap(subgroup => setGroupFeatures(subgroup, showAdvancedDeviceLevel))
                    }
                });
            }

            if (!existing_group_layer) {
                map.current.addLayer({
                    id: 'subgroups-layer',
                    type: 'line',
                    source: 'subgroups-source',
                    paint: {
                        "line-color": ['get', 'feature_colour'],
                        "line-width": 5,
                    }
                });
            }

            map.current.on('click', 'subgroups-layer', handleSubgroupClick);
            map.current.on('mouseenter', 'subgroups-layer', handleSubgroupMouseEnter);
            map.current.on('mouseleave', 'subgroups-layer', handleSubgroupMouseLeave);
        }

        map.current.on('click', 'devices-layer', handleDeviceClick);
        map.current.on('mouseenter', 'devices-layer', handleDeviceMouseEnter);
        map.current.on('mouseleave', 'devices-layer', handleDeviceMouseLeave);

        map.current.on('click', handleMapClick);

        return () => {
            map.current.off('click', 'subgroups-layer', handleSubgroupClick);
            map.current.off('mouseenter', 'subgroups-layer', handleSubgroupMouseEnter);
            map.current.off('mouseleave', 'subgroups-layer', handleSubgroupMouseLeave);

            map.current.off('click', 'devices-layer', handleDeviceClick);
            map.current.off('mouseenter', 'devices-layer', handleDeviceMouseEnter);
            map.current.off('mouseleave', 'devices-layer', handleDeviceMouseLeave);

            map.current.off('click', handleMapClick);
        }

    }, [showDevices, groups_data]);

    useEffect(() => {
        if (!map.current) return;

        map.current.setFilter('devices-layer', showDevices ? ['all'] : ['==', ['id'], '']);
        map.current.setFilter('subgroups-layer', !showDevices ? ['all'] : ['==', ['id'], '']);

    }, [showDevices]);

    useEffect(() => () => { //unmount
        if (groups_data.subgroups.length > 0) {
            map.current.removeLayer('subgroups-layer');
            map.current.removeSource('subgroups-source');
        }

        map.current.removeLayer('groups-layer-liner');
        map.current.removeLayer('groups-layer');
        map.current.removeSource('groups-source');

        map.current.removeLayer('devices-layer');
        map.current.removeSource('devices-source');

        map.current.getCanvas().style.cursor = '';
        popup.remove();

    }, []);
}

ClearwaysSubgroupsMap.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    groups_data: PropTypes.array,
    showDevices: PropTypes.bool,
};

export default ClearwaysSubgroupsMap;